/* ============================================================================
 * <FormModal> — Floating modal estilo Alvotec for cadastros simples.
 *
 * Layout: header (icone + titulo + X) | tabs (opcional) | body | footer (Salvar/Fechar).
 * Optimized for narrow forms (max-w-2xl) that are pure CRUD; reuse instead of
 * inline accordion forms.
 *
 * Usage:
 *   <FormModal
 *     open={open}
 *     title={editing ? 'Editar Conta' : 'Inserir Conta'}
 *     icon={<Wallet size={18} />}
 *     tabs={[{ key: 'basic', label: 'Dados Basicos' }, { key: 'extras', label: 'Outros' }]}
 *     activeTab={tab}
 *     onTabChange={setTab}
 *     onClose={() => setOpen(false)}
 *     onSave={handleSave}
 *     submitting={submitting}
 *     saveLabel={editing ? 'Atualizar' : 'Salvar'}
 *     testId="bank-account-modal"
 *   >
 *     {tab === 'basic' && <BasicFields .../>}
 *     {tab === 'extras' && <ExtraFields .../>}
 *   </FormModal>
 * ============================================================================ */
import React from 'react';
import { X, Save } from 'lucide-react';

export const FormModal = ({ open, title, icon, tabs = null, activeTab, onTabChange, onClose, onSave, children, submitting = false, saveLabel = 'Salvar', testId = 'form-modal', headerVariant = 'default' }) => {
  if (!open) return null;
  const headerBg = headerVariant === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : headerVariant === 'danger' ? 'bg-rose-50 border-rose-200 text-rose-800'
    : 'bg-violet-50 border-violet-200 text-violet-800';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid={testId}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className={`px-5 py-3 flex items-center justify-between border-b ${headerBg}`}>
          <h2 className="font-black text-base flex items-center gap-2">
            {icon}{title}
          </h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" data-testid={`${testId}-close`}><X size={18} /></button>
        </div>
        {tabs && tabs.length > 1 && (
          <div className="flex gap-1 px-5 pt-3 border-b border-slate-200 bg-slate-50">
            {tabs.map(t => (
              <button key={t.key} onClick={() => onTabChange(t.key)} data-testid={`${testId}-tab-${t.key}`}
                className={`px-4 py-2 text-xs font-bold rounded-t-lg transition-colors border-b-2 ${activeTab === t.key ? 'text-violet-700 border-violet-600 bg-white' : 'text-slate-500 border-transparent hover:text-slate-800'}`}>
                {t.icon && <span className="mr-1.5 inline-flex align-middle">{t.icon}</span>}
                {t.label}
              </button>
            ))}
          </div>
        )}
        <form onSubmit={(e) => { e.preventDefault(); onSave?.(); }} className="flex-1 overflow-y-auto p-5">
          {children}
        </form>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose} type="button" className="btn-pill-ghost" data-testid={`${testId}-cancel`}>Fechar</button>
          <button onClick={onSave} disabled={submitting} className="btn-pill-primary disabled:opacity-50" data-testid={`${testId}-save`}>
            <Save size={13} />{submitting ? 'Salvando...' : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormModal;
